/**
 * 購物車狀態管理
 * 使用 React Context 和 useReducer 實現輕量級狀態管理
 * 支援 localStorage 持久化（災難恢復）
 */

import { createContext, useReducer, ReactNode, useEffect } from 'react';
import { z } from 'zod';
import { STORAGE_KEYS } from '../utils/storage/storageKeyFactory';
import {
  getWithExpiry,
  setWithExpiry,
  removeItem as removeStorageItem,
} from '../utils/storage/storageUtils';

/**
 * 購物車商品項目
 * id：唯一鍵（單品 = productId，規格品 = `${productId}:${productVariantId}`）
 * productId：商品 ID（結帳 API 必填；向後兼容未填時以 id 當 product_id）
 * productVariantId：規格 ID（規格品時必填，結帳 API 選填）
 * variantLabel：規格顯示字串（如 "尺寸: M / 顏色: 紅"），僅用於 UI 顯示
 * productType：商品類型（電子券時用於顯示「設定適用日期/時段」）
 * applicable_dates / applicable_time_starts / applicable_time_ends：僅電子券；長度須為 quantity
 */
export interface CartItem {
  id: number | string;
  productId?: string; // 結帳時傳 product_id；向後兼容
  productVariantId?: string; // 規格品時傳 product_variant_id
  variantLabel?: string; // 規格顯示用
  productType?: string; // physical | service | voucher | subscription | bundle
  /** 電子券適用日期（YYYY-MM-DD），第 i 張對應 [i] */
  applicable_dates?: string[];
  /** 電子券適用時段起（HH:mm），須與 applicable_time_ends 成對 */
  applicable_time_starts?: string[];
  /** 電子券適用時段訖（HH:mm），須與 applicable_time_starts 成對 */
  applicable_time_ends?: string[];
  name: string;
  price: number;
  thumbnail?: string;
  quantity: number;
}

/**
 * 購物車狀態
 */
export interface CartState {
  items: CartItem[];
  totalAmount: number;
  itemCount: number;
}

/**
 * 購物車商品項目 Zod Schema
 */
export const CartItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  productId: z.string().optional(),
  productVariantId: z.string().optional(),
  variantLabel: z.string().optional(),
  productType: z.string().optional(),
  applicable_dates: z.array(z.string()).optional(),
  applicable_time_starts: z.array(z.string()).optional(),
  applicable_time_ends: z.array(z.string()).optional(),
  name: z.string(),
  price: z.number(),
  thumbnail: z.string().optional(),
  quantity: z.number().min(1),
});

/**
 * 購物車狀態 Zod Schema
 */
export const CartStateSchema = z.object({
  items: z.array(CartItemSchema),
  totalAmount: z.number(),
  itemCount: z.number(),
});

/**
 * 購物車操作類型
 */
export type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity'> }
  | { type: 'REMOVE_ITEM'; payload: number | string }
  | {
      type: 'UPDATE_QUANTITY';
      payload: { id: number | string; quantity: number };
    }
  | { type: 'CLEAR_CART' };

/**
 * 購物車 Context
 */
export interface CartContextType {
  state: CartState;
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: number | string) => void;
  updateQuantity: (id: number | string, quantity: number) => void;
  clearCart: () => void;
}

export const CartContext = createContext<CartContextType | undefined>(
  undefined
);

/**
 * 購物車 Reducer
 */
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(
        item => item.id === action.payload.id
      );

      if (existingItem) {
        // 如果商品已存在，增加數量
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );

        return {
          ...state,
          items: updatedItems,
          totalAmount: updatedItems.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          ),
          itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
        };
      } else {
        // 新增商品
        const newItem: CartItem = { ...action.payload, quantity: 1 };
        const updatedItems = [...state.items, newItem];

        return {
          ...state,
          items: updatedItems,
          totalAmount: updatedItems.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          ),
          itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
        };
      }
    }

    case 'REMOVE_ITEM': {
      const updatedItems = state.items.filter(
        item => item.id !== action.payload
      );

      return {
        ...state,
        items: updatedItems,
        totalAmount: updatedItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        ),
        itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
      };
    }

    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        // 數量為 0 或負數時移除商品
        return cartReducer(state, {
          type: 'REMOVE_ITEM',
          payload: action.payload.id,
        });
      }

      const updatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );

      return {
        ...state,
        items: updatedItems,
        totalAmount: updatedItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        ),
        itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
      };
    }

    case 'CLEAR_CART':
      return {
        items: [],
        totalAmount: 0,
        itemCount: 0,
      };

    default:
      return state;
  }
}

/**
 * 購物車 Provider 組件
 */
export function CartProvider({ children }: { children: ReactNode }) {
  // 初始化狀態的函數：嘗試從 localStorage 讀取
  const getInitialState = (): CartState => {
    const defaultState: CartState = {
      items: [],
      totalAmount: 0,
      itemCount: 0,
    };

    if (typeof window === 'undefined') {
      return defaultState;
    }

    try {
      const saved = getWithExpiry<CartState>(
        STORAGE_KEYS.POS_CART_BACKUP(),
        defaultState,
        {
          schema: CartStateSchema,
          scope: 'tenant',
        }
      );

      // 驗證保存的資料是否有效
      if (saved.items.length > 0) {
        // 重新計算總金額和數量（確保一致性）
        const totalAmount = saved.items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        );
        const itemCount = saved.items.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        return {
          items: saved.items,
          totalAmount,
          itemCount,
        };
      }
    } catch (error) {
      console.error('[CartProvider] 從 localStorage 讀取購物車失敗:', error);
    }

    return defaultState;
  };

  // 使用 lazy initialization
  const [state, dispatch] = useReducer(cartReducer, getInitialState());

  // 當狀態改變時，寫入 localStorage
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // 確保 state 是有效的物件，不是函數或其他類型
    if (typeof state !== 'object' || state === null || Array.isArray(state)) {
      console.warn('[CartProvider] state 不是有效的物件，跳過儲存:', state);
      return;
    }

    try {
      setWithExpiry(STORAGE_KEYS.POS_CART_BACKUP(), state, {
        schema: CartStateSchema,
        scope: 'tenant',
      });
    } catch (error) {
      console.error('[CartProvider] 寫入購物車到 localStorage 失敗:', error);
    }
  }, [state]);

  const addItem = (item: Omit<CartItem, 'quantity'>) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const removeItem = (id: number | string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateQuantity = (id: number | string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    // 明確清除 localStorage 中的購物車備份
    if (typeof window !== 'undefined') {
      try {
        removeStorageItem(STORAGE_KEYS.POS_CART_BACKUP(), 'tenant');
      } catch (error) {
        console.error('[CartProvider] 清除購物車 localStorage 失敗:', error);
      }
    }
  };

  return (
    <CartContext.Provider
      value={{ state, addItem, removeItem, updateQuantity, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Hook 已移至 hooks/useCart.ts
